import crmArabicCatalog from './crmCatalog.json';
import crmEnglishAliases from './crmEnglishAliases.json';
import { receptionArabicCatalog, receptionEnglishCatalog } from '../../reception/locale/catalog';
import { salesArabicCatalog, salesEnglishCatalog } from '../../pages/sales/locale/catalog';
import { localizeJobTitle } from './jobTitles';
import sharedSupplement from './sharedSupplement.json';
import operationsSupplement from './operationsSupplement.json';
import localizationEditorCopy from './localizationEditorCopy.json';
import businessCatalog from './businessCatalog.json';
import summerSupplement from './summerSupplement.json';
import { staffLoginCatalog, type StaffLoginCopy } from './staffLoginCatalog';

export type AppLanguage = 'en' | 'ar';
export type LocaleNamespace = 'common' | 'crm' | 'reception' | 'sales' | 'auth';
export interface LocalizedMessage { en: string; ar: string }

/** Semantic keys for new UI. Existing authored-copy catalogs remain lossless adapters. */
const commonMessages: Record<string, LocalizedMessage> = {
  'common.save': { en: 'Save', ar: 'حفظ' },
  'common.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'common.close': { en: 'Close', ar: 'إغلاق' },
  'common.open': { en: 'Open', ar: 'فتح' },
  'common.search': { en: 'Search', ar: 'بحث' },
  'common.loading': { en: 'Loading...', ar: 'جارٍ التحميل...' },
  'common.retry': { en: 'Retry', ar: 'إعادة المحاولة' },
  'common.confirm': { en: 'Confirm', ar: 'تأكيد' },
  'common.actions': { en: 'Actions', ar: 'الإجراءات' },
  'common.yes': { en: 'Yes', ar: 'نعم' },
  'common.no': { en: 'No', ar: 'لا' },
  'common.noResults': { en: 'No results found', ar: 'لا توجد نتائج' },
  'common.select': { en: 'Select...', ar: 'اختار' },
  'common.clearSelection': { en: 'Clear selection', ar: 'مسح الاختيار' },
  'common.availableOptions': { en: 'Available options', ar: 'الخيارات المتاحة' },
  'common.noMatchingResults': { en: 'No matching results', ar: 'لا توجد نتائج مطابقة' },
  'common.requiredSelection': { en: 'Please select an option', ar: 'يرجى اختيار قيمة' },
  'common.clearSearch': { en: 'Clear search', ar: 'مسح البحث' },
  'common.brand': { en: 'Arabian Academy', ar: 'Arabian Academy' },
  'common.brandMark': { en: 'A', ar: 'A' },
  'common.portalTeams': { en: 'Portal teams', ar: 'فرق الأكاديمية' },
};
export const appLanguageOptions = (['en', 'ar'] as const).map(value => ({value, label:value.toUpperCase()}));
const authPairs: [string,string][] = Object.entries(staffLoginCatalog.en).flatMap(([field, en]) => {
  const ar = staffLoginCatalog.ar[field as keyof typeof staffLoginCatalog.ar];
  return typeof en === 'string' && typeof ar === 'string' ? [[en, ar] as [string,string]]
    : Array.isArray(en) && Array.isArray(ar) ? en.map((text,index) => [text, ar[index]] as [string,string]) : [];
});
// Supplemental authored copy fills gaps; established department terminology
// always wins when a source phrase has different meanings across workspaces.
const supplementalArabic = { ...sharedSupplement, ...operationsSupplement, ...summerSupplement, ...localizationEditorCopy, ...businessCatalog };
const supplementalEnglish = Object.fromEntries(Object.entries(supplementalArabic).map(([en, ar]) => [ar, en]));
const arabicCatalogs: Record<LocaleNamespace, Readonly<Record<string, string>>> = {
  common: Object.fromEntries(Object.values(commonMessages).map(message => [message.en, message.ar])),
  crm: { ...supplementalArabic, ...crmArabicCatalog },
  reception: { ...supplementalArabic, ...receptionArabicCatalog },
  sales: { ...supplementalArabic, ...salesArabicCatalog },
  auth: Object.fromEntries(authPairs),
};
const englishCatalogs: Record<LocaleNamespace, Readonly<Record<string, string>>> = {
  common: Object.fromEntries(Object.values(commonMessages).map(message => [message.ar, message.en])),
  crm: { ...supplementalEnglish, ...Object.fromEntries(Object.entries(crmArabicCatalog).map(([en, ar]) => [ar, en])), ...crmEnglishAliases },
  reception: { ...supplementalEnglish, ...receptionEnglishCatalog },
  sales: { ...supplementalEnglish, ...salesEnglishCatalog },
  auth: Object.fromEntries(authPairs.map(([en,ar]) => [ar,en])),
};

/** Copy-derived IDs are deterministic, collision-free and scoped to their domain. */
export function getMessageKey(namespace: LocaleNamespace, englishCopy: string): string {
  const common = Object.entries(commonMessages).find(([, message]) => message.en === englishCopy);
  return namespace === 'common' && common ? common[0] : `${namespace}.copy.${encodeURIComponent(englishCopy)}`;
}
const registry: Record<string, Readonly<LocalizedMessage>> = Object.create(null);
for (const namespace of Object.keys(arabicCatalogs) as LocaleNamespace[]) {
  for (const [en, ar] of Object.entries(arabicCatalogs[namespace])) {
    registry[getMessageKey(namespace, en)] = Object.freeze({ en, ar });
  }
}
for (const [field, en] of Object.entries(staffLoginCatalog.en)) {
  const ar = staffLoginCatalog.ar[field as keyof typeof staffLoginCatalog.ar];
  if (typeof en === 'string' && typeof ar === 'string') registry[`auth.staffLogin.${field}`] = Object.freeze({en,ar});
  else if (Array.isArray(en) && Array.isArray(ar)) en.forEach((text,index) => {
    registry[`auth.staffLogin.${field}.${index}`] = Object.freeze({en:text,ar:ar[index]});
  });
}
export const localizationRegistry: Readonly<Record<string, Readonly<LocalizedMessage>>> = Object.freeze(registry);
const missingKeys = new Set<string>();
// Existing APIs use different casing for these same branch identities.
// Canonicalize presentation only; option values and request parameters stay raw.
const branchCaseAliases = new Map(['Sporting', 'Miami', 'Dokki', 'Nasr City', 'Online'].map(value => [value.toLowerCase(), value]));
export function getMissingTranslationKeys(): readonly string[] { return [...missingKeys].sort(); }
export function clearMissingTranslationKeys(): void { missingKeys.clear(); }
export function hasTranslationKey(key: string): boolean { return Object.hasOwn(localizationRegistry, key); }

export function interpolateMessage(template: string, values: readonly unknown[] = []): string {
  return template.replace(/\{(\d+)\}/g, (placeholder, index: string) => Number(index) < values.length ? String(values[Number(index)]) : placeholder);
}
/** Missing-key reporting is explicit: unknown customer text never enters this registry. */
export function translateKey(key: string, language: AppLanguage, values: readonly unknown[] = [], fallback = key): string {
  if (!hasTranslationKey(key)) {
    missingKeys.add(key);
    return interpolateMessage(fallback, values);
  }
  return interpolateMessage(localizationRegistry[key][language], values);
}

export function getStaffLoginCopy(language: AppLanguage): StaffLoginCopy {
  return Object.fromEntries(Object.entries(staffLoginCatalog.en).map(([field, value]) => [field,
    Array.isArray(value) ? value.map((_, index) => translateKey(`auth.staffLogin.${field}.${index}`, language))
      : translateKey(`auth.staffLogin.${field}`, language)])) as StaffLoginCopy;
}

interface TextTemplate { pattern: RegExp; target: string; indexes: number[] }
function compileTemplates(catalog: Readonly<Record<string, string>>, minimumLiteralLength: number): TextTemplate[] {
  return Object.entries(catalog)
    .filter(([source]) => /\{\d+\}/.test(source) && source.replace(/\{\d+\}/g, '').trim().length >= minimumLiteralLength)
    .sort(([left], [right]) => right.replace(/\{\d+\}/g, '').length - left.replace(/\{\d+\}/g, '').length)
    .map(([source, target]) => {
      const indexes: number[] = [];
      let pattern = '';
      let end = 0;
      for (const match of source.matchAll(/\{(\d+)\}/g)) {
        pattern += source.slice(end, match.index).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const index = Number(match[1]);
        const previous = indexes.indexOf(index);
        if (previous === -1) { indexes.push(index); pattern += '([\\s\\S]*?)'; }
        else pattern += `\\${previous + 1}`;
        end = match.index + match[0].length;
      }
      pattern += source.slice(end).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return { pattern: new RegExp(`^${pattern}$`), target, indexes };
    });
}
const templates = Object.fromEntries((Object.keys(arabicCatalogs) as LocaleNamespace[]).map(namespace => [namespace, {
  en: compileTemplates(englishCatalogs[namespace], namespace === 'reception' ? 1 : 12),
  ar: compileTemplates(arabicCatalogs[namespace], namespace === 'reception' ? 1 : 12),
}])) as Record<LocaleNamespace, Record<AppLanguage, TextTemplate[]>>;
function matchTemplate(text: string, candidates: readonly TextTemplate[]): string | undefined {
  for (const { pattern, target, indexes } of candidates) {
    const match = pattern.exec(text);
    if (match) return target.replace(/\{(\d+)\}/g, (placeholder, index: string) => {
      const captureIndex = indexes.indexOf(Number(index));
      return captureIndex === -1 ? placeholder : match[captureIndex + 1];
    });
  }
}

/** Compatibility for authored UI copy only. Never call this for record names or notes. */
export function translateUiText(text: string, language: AppLanguage, namespace: LocaleNamespace = 'crm'): string {
  if (hasTranslationKey(text)) return translateKey(text, language);
  const trimmed = text.trim();
  const english = englishCatalogs[namespace];
  const arabic = arabicCatalogs[namespace];
  const canonical = Object.hasOwn(english, trimmed) ? english[trimmed] : matchTemplate(trimmed, templates[namespace].en) ?? branchCaseAliases.get(trimmed.toLowerCase()) ?? trimmed;
  const title = namespace === 'crm' ? localizeJobTitle(canonical, language) : canonical;
  if (title !== canonical) return text.replace(trimmed, () => title);
  const translated = language === 'en' ? canonical : Object.hasOwn(arabic, canonical) ? arabic[canonical] : matchTemplate(canonical, templates[namespace].ar);
  return translated === undefined ? text : text.replace(trimmed, () => translated);
}
export function formatUiText(template: string, values: readonly unknown[], language: AppLanguage, namespace: LocaleNamespace = 'crm'): string {
  return interpolateMessage(translateUiText(template, language, namespace), values);
}
export function formatLocaleNumber(value: number, language: AppLanguage, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-GB', options).format(value);
}
export function formatLocaleDate(value: Date | number, language: AppLanguage, options?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'en-GB', options).format(value);
}
